// GMA-1318: in-page audible alert for newly-arrived critical (fall) sensor
// notifications.
//
// The web-push / service-worker path plays the OS notification sound when the
// CompassCare tab is backgrounded. This module covers the tab-open case, where
// the bell (NotificationAlert) polls new alerts in silently — QA (GMA-1318)
// reported a fall showed in the bell and email but made no sound.
//
// It uses the Web Audio API to synthesize a short two-tone beep, so there is no
// binary audio asset to ship, and it is a hard no-op wherever Web Audio is
// unavailable (jsdom under test, unsupported browsers) so it can never throw
// into the bell's polling path.

let audioCtx = null;
let gestureUnlockBound = false;

function getAudioContextCtor() {
  if (typeof window === "undefined") return null;
  return window.AudioContext || window.webkitAudioContext || null;
}

export function isAudioSupported() {
  return getAudioContextCtor() !== null;
}

function ensureContext() {
  if (audioCtx) return audioCtx;
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    audioCtx = new Ctor();
  } catch (_err) {
    audioCtx = null;
  }
  return audioCtx;
}

// Chrome's autoplay policy starts an AudioContext "suspended" until a user
// gesture resumes it. Bind one-time document gesture listeners so the first
// beep after any caregiver interaction (which they will have made by the time a
// fall fires) is audible. Idempotent — safe to call on every mount.
export function unlockCriticalAlertAudio() {
  if (gestureUnlockBound || typeof document === "undefined") return;
  gestureUnlockBound = true;
  const resume = () => {
    const ctx = ensureContext();
    if (ctx && ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => {});
    }
    // Genuinely one-time: the first gesture has now created/resumed the
    // context, so drop both listeners instead of leaving them firing resume()
    // on every later interaction for the life of the page.
    try {
      document.removeEventListener("pointerdown", resume);
      document.removeEventListener("keydown", resume);
    } catch (_err) {
      // ignore
    }
  };
  try {
    document.addEventListener("pointerdown", resume);
    document.addEventListener("keydown", resume);
  } catch (_err) {
    // ignore — a missing addEventListener must never break the bell
  }
}

// Play a short, attention-getting two-tone beep for a critical alert. Returns
// true if playback was started, false if Web Audio is unavailable. Never throws.
export function playCriticalAlertTone() {
  const ctx = ensureContext();
  if (!ctx) return false;
  try {
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => {});
    }
    const now = typeof ctx.currentTime === "number" ? ctx.currentTime : 0;
    const beep = (startOffset, freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      // Quick attack, short decay — audible but not jarring.
      gain.gain.setValueAtTime(0.0001, now + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.3, now + startOffset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + startOffset);
      osc.stop(now + startOffset + 0.36);
    };
    beep(0, 880); // A5
    beep(0.4, 988); // B5
    return true;
  } catch (_err) {
    return false;
  }
}
